import genUniqueId from './uniqueId';

const SingletonIntersectionObserver = (() => {
	const elements = {};
	let defaultIO;
	const runCallbackIfIntersecting = callback => {
		return entries => {
			entries.forEach(entry => {
				if (entry.isIntersecting) {
					try {
						callback();
					} catch (e) {}
				}
			});
		};
	};
	const DEFAULT_THRESHOLD = 0;
	// threshold here means the percentage of the target (elem in our
	// case) that's intersecting the `root` item we've provided. Since
	// we want to trigger the callback pre-emptively (like 350px below
	// the viewport, we need to "extend" the intersection area by that
	// amount. This can be achived by the `rootMargin` parameter.
	//
	// In summary, we call the callback when the `elem` crosses 0.01%
	// intersection with viewport, extended by 350px downwards.
	const defaultOptions = {
		root: undefined,
		rootMargin: '0px 0px ' + DEFAULT_THRESHOLD + 'px 0px',
		threshold: 0.01
	};
	// Cached intersection observer instance for use with default options. If
	// either the root object, or the rootMargin changed, we have to use a
	// different intersection observer for those settings. This is to avoid too
	// many IO objects getting created in a hot loop.
	//
	// TODO: Will this break if IO is not natively supported, and if this is
	// used inside SSR functionality?
	defaultIO =
		defaultIO ||
		function (callback) {
			return new IntersectionObserver(runCallbackIfIntersecting(callback), defaultOptions);
		};
	const newIO = function (callback, container, threshold) {
		const options = Object.assign({}, defaultOptions, {
			root: container,
			rootMargin: '0px 0px ' + (threshold || DEFAULT_THRESHOLD) + 'px 0px'
		});
		return new IntersectionObserver(runCallbackIfIntersecting(callback), options);
	};
	return {
		register: function (elem, callback, container, threshold) {
			if (!callback) {
				callback = x => {
					return x;
				};
			}
			// This function delegates to Intersection Observer api
			if (!elem) return null;
			const observer = !threshold && !container ? defaultIO(callback) : newIO(callback, container, threshold);
			observer.observe(elem);
			const id = genUniqueId();
			// storing the observer also, because the element will be stored
			// per intersection observer object, and to "unobserve", we have to
			// call the function only on the correct intersection observer.
			elements[id] = { elem: elem, observer: observer };
			return id;
		},
		unregister: function (id) {
			if (!id) return;
			const element = elements[id];
			if (element) {
				const observer = elements[id].observer;
				if (observer) {
					observer.unobserve(elements[id].elem);
				}
			}
			delete elements[id];
			// FIXME: Do we have to set `elements[id]` to null, now that we
			// have added an extra reference of observer in the object, some
			// of which can be referenced by other nodes in this object?
		}
	};
})();
export default SingletonIntersectionObserver;
